import AutoNumeric from 'autonumeric';
import { FormatHelpers } from "../helpers/format-helpers";
import { StringHelpers } from '../helpers/string-helpers';
import { TraceLoggingHelpers } from '../helpers/trace-logging-helpers';
import { TradeOrderMessageHelpers } from '../helpers/trade-order-message-helpers';
import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartSettingsPopupModel } from '../models/chart-style/chart-settings-popup.model';
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { TIChartPointModel } from '../models/data-point/ti-chart-point.model';
import { Timeframe } from '../models/enum/timeframe.enum';
import { TradeAccountModel } from '../models/trade/trade-account.model';
import { QuoteType, TradeInForceType, TradeModel, TradeOrderType } from "../models/trade/trade.model";
import { IComponent } from "./component.interface";
import { InformationPopupComponent, InformationPopupType } from './information-popup.component';
import { PnlComponent } from './pnl.component';
import { ObjectHelpers } from '../helpers/object-helpers';

export class TradeComponent implements IComponent {
    public id = '';

    public enabled = false;

    private element: HTMLElement;
    private submitting = false;
    private dragging = false;
    private moving = false;
    private accountRendered = false;
    private preventPersistingAccount = false;
    private preventPersistingShares = false;

    private base: IChartContainer;
    private chartStyle: ChartStyleSettingsModel | null = null;

    private quoteQuantityInput: AutoNumeric | null = null;
    private pricesInput: AutoNumeric | null = null;

    private accountSelectListElement: HTMLElement | null = null;
    private priceLabelElement: HTMLElement | null = null;
    private submitButtonElement: HTMLElement | null = null;

    private model = new TradeModel();
    private accounts: Array<TradeAccountModel> = [];
    private changeCallbacks: Array<Function> = [];

    private readonly DEFAULT_BROWSER_TOP = '200px';
    private readonly DEFAULT_BROWSER_LEFT = '20px';

    private readonly DEFAULT_TOP = '200px';
    private readonly DEFAULT_LEFT = '20px';

    constructor(base: IChartContainer) {
        this.id = StringHelpers.newGuid();
        this.base = base;

        this.subscribeChartDataService();
        this.renderHtml();
        this.getGeneratedElementBeforeInit(0);
    }

    public show() {
        this.addActive();
        this.saveUIState();
    }

    public hide() {
        this.element.classList.remove('active');
        this.saveUIState();
    }

    public isActive(): boolean {
        return this.element.classList.contains('active');
    }

    public getTradeModel(): TradeModel {
        return this.model;
    }

    public getAccountName(): string {
        return this.model?.account ?? '';
    }

    public setAccount(value: string) {
        const acccountElement = this.accountSelectListElement.querySelector<HTMLElement>('li[data-value="' + value + '"]');
        if (acccountElement) {
            this.preventPersistingAccount = true;
            this.selectAccount(acccountElement);
        }
    }

    public setShares(value: number) {
        if (value <= 0 || document.activeElement === this.quoteQuantityInput.node() || this.submitting) return;

        this.preventPersistingShares = true;
        this.setQuoteType(QuoteType.Shares);
        this.setQuoteQuantity(value);
        this.notifyChangeCallbacks();
    }

    public setSharesAndPersisting(value: number) {
        if (value <= 0 || document.activeElement === this.quoteQuantityInput.node() ||  this.submitting) return;

        this.preventPersistingShares = false;
        this.setQuoteType(QuoteType.Shares);
        this.setQuoteQuantity(value);
        this.notifyChangeCallbacks();
    }

    public setTradeType(value: TradeOrderType) {
        const op = this.element.querySelector<HTMLElement>(`.trade-options .opt[data-value="${value}"]`);
        this.selectTradeOption(op);
    }

    public setBuyOrSell(buy: boolean) {
        if (buy) {
            this.setBuy();
        } else {
            this.setSell();
        }
    }

    public getQuoteQuantity(): number | null {
        return this.quoteQuantityInput?.getNumber();
    }

    public setQuoteQuantity(value: number) {
        this.model.quoteQuantity = value;
        this.quoteQuantityInput?.set(value);
        this.onModelChange(false);
    }

    public getQuoteType(): QuoteType {
        return this.model.quoteType;
    }

    public setQuoteType(quoteType: QuoteType) {
        this.model.quoteType = quoteType;
        this.setSelectListValue('.select-list.quote-type li', this.model.quoteType);
        this.onModelChange(false);
    }

    public onChange(callback: Function) {
        if (callback)
            this.changeCallbacks.push(callback);
    }

    public saveQuoteQuantityToDefault() {
        if (!this.base.marketingWebChartMode && !this.preventPersistingShares && this.chartStyle && this.chartStyle.buySellSyncTradeSizeEnabled && this.chartStyle.chartSettingsPopupModel) {
            if (this.model.quoteQuantity != this.chartStyle.chartSettingsPopupModel.defaultQuoteQuantity || this.model.quoteType != this.chartStyle.chartSettingsPopupModel.defaultQuoteType) {
                this.chartStyle.chartSettingsPopupModel.defaultQuoteQuantity = this.model.quoteQuantity;
                this.chartStyle.chartSettingsPopupModel.defaultQuoteType = this.model.quoteType;
                this.base.chartService.requestSaveChartSettingsPopupModel(this.chartStyle.chartSettingsPopupModel);
            }
        }
    }

    private notifyChangeCallbacks() {
        for (const callback of this.changeCallbacks) {
            callback();
        }
    }

    private addActive() {
        if (!this.enabled) return;

        this.element.classList.add('active');
        this.base.chartService.requestOpenRobotTrade();
    }

    private getGeneratedElementBeforeInitTimer: any;
    private getGeneratedElementBeforeInit(retryCount: number) {
        if (retryCount > 50) {
            TraceLoggingHelpers.log('TradeComponen, cannot get elements with query selector');
        }

        this.accountSelectListElement = this.element.querySelector('.select-list.accounts')
        this.priceLabelElement = this.element.querySelector('.buy-sell .value')
        this.submitButtonElement = this.element.querySelector('.submit-button');

        if (this.accountSelectListElement && this.priceLabelElement && this.submitButtonElement) {
            this.init();
        } else {
            clearTimeout(this.getGeneratedElementBeforeInitTimer);
            this.getGeneratedElementBeforeInitTimer = setTimeout(() => {
                this.getGeneratedElementBeforeInit(retryCount++);
            }, 500);
        }
    }

    private init() {
        this.initAutoNumberic();
        this.onPriceLabelClick();
        this.onPricesKeyup();
        this.onSharesAmountKeyup();
        this.onBuySellClick();
        this.onTradeOptionsClick();
        this.onSelectListClick();
        this.onSelectListItemClick();
        this.onSubmitButtonClick();
        this.onDrag();
        this.onResize();
        this.onExtreamChange();
        this.onBodyClick();

        this.setDefaults();
    }

    private onModelChange(notifyToCallback: boolean) {
        if (this.model.isBuy) {
            this.element.classList.remove('sell');
            this.element.classList.add('buy');
        } else {
            this.element.classList.remove('buy');
            this.element.classList.add('sell');
        }

        this.setSubmitButtonText();
        this.saveUIState();

        if (notifyToCallback) {
            this.notifyChangeCallbacks();
        }

        this.saveQuoteQuantityToDefault();
    }

    private subscribeChartDataService() {
        this.base.chartService.subscribeChartStyleData((data: ChartStyleSettingsModel) => {
            let needToSet = false;
            if (!this.chartStyle || this.chartStyle.chartSettingsPopupModel?.defaultEnableTradeOrderEntry != this.enabled) {
                needToSet = true;
            }

            this.chartStyle = data;

            if (needToSet)
                this.setPropertiesFromChartStyles();
        });
        this.base.chartService.subscribeLastPriceData((price: number) => {
            this.model.defaultPrice = price;
            this.updatePriceLabel();
        });
        this.base.chartService.subscribe_TradeAccounts((accounts: Array<TradeAccountModel>) => {
            this.accounts = accounts;
            this.renderAccountList();
        });
        this.base.chartService.subscribe_LastTradeAccounts((account: string) => {
            this.setAccount(account);
            this.setPnlLastTradeAccount();
        });
        this.base.chartService.subscribeInitChartData((data: Array<TIChartPointModel>, timeframe: Timeframe, symbol: string) => {
            const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
            if (popup.isActive()) {
                popup.close();
            }

            setTimeout(() => {
                this.model.price = this.base.tiChartSeries.lastDataPoint?.close ?? 0;
                this.model.defaultPrice = this.model.price;
                if (this.model.orderType != TradeOrderType.Market)
                    this.pricesInput.set(this.model.price);

                this.setSubmitButtonText();
            }, 100);
        });
        this.base.chartService.subscribeChartRequestingNewSymbolData(() => {
            if (!this.enabled) return;

            let chartSettingsPopupModel = this.base.tiChartSeries?.chartStyleSettings?.chartSettingsPopupModel;
            if (!chartSettingsPopupModel) return;

            if (chartSettingsPopupModel.defaultQuoteType) {
                this.model.defaultQuoteType = chartSettingsPopupModel.defaultQuoteType as QuoteType;
                this.setQuoteType(this.model.defaultQuoteType);
            }

            if (chartSettingsPopupModel.defaultQuoteQuantity) {
                this.model.quoteQuantity = chartSettingsPopupModel.defaultQuoteQuantity;
                this.setQuoteQuantity(this.model.quoteQuantity);
            }

            this.notifyChangeCallbacks();
        });
    }

    private setPropertiesFromChartStyles() {
        if (this.chartStyle.tradeOrderEntrySupported && this.chartStyle.tradeOrderEntryEnabled && this.chartStyle.chartSettingsPopupModel?.defaultEnableTradeOrderEntry) {
            this.base.element.addClass('new-trade-ui');
            this.enabled = true;
        } else {
            this.base.element.removeClass('new-trade-ui');
            this.enabled = false;
            this.hide();
        }

        if (!this.enabled) return;
        if (this.chartStyle.newTradeUILeft != null && this.chartStyle.newTradeUITop != null) {
            this.element.style.left = this.chartStyle.newTradeUILeft + 'px';
            this.element.style.top = this.chartStyle.newTradeUITop + 'px';
        }

        if (this.chartStyle.newTradeUITIF) {
            this.model.defaultTIF = this.chartStyle.newTradeUITIF as TradeInForceType;
        }

        if (this.chartStyle.chartSettingsPopupModel?.defaultQuoteType) {
            this.model.defaultQuoteType = this.chartStyle.chartSettingsPopupModel?.defaultQuoteType as QuoteType;
        }

        if (this.chartStyle.chartSettingsPopupModel?.defaultQuoteQuantity) {
            this.model.quoteQuantity = this.chartStyle.chartSettingsPopupModel?.defaultQuoteQuantity;
        }

        if (this.chartStyle.newTradeUIOpen) {
            this.addActive();
        }

        this.setAccountFromChartStyles();

        this.setDefaults();
    }

    private setAccountFromChartStyles() {
        if (this.chartStyle) {
            let accountElement: HTMLElement | null = null;
            if (this.chartStyle.newTradeUIAccount) {
                accountElement = this.accountSelectListElement.querySelector<HTMLElement>('li[data-value="' + this.chartStyle.newTradeUIAccount + '"]');
            }

            //fallback to first account
            if (!accountElement) {
                accountElement = this.accountSelectListElement.querySelector<HTMLElement>('li');
            }

            if (accountElement) {
                this.selectAccount(accountElement);
            }
        }
    }

    private renderAccountList() {
        if (!this.accounts?.length || this.accountSelectListElement.classList.contains('active')) {
            return;
        }

        let html = '';
        for (let i = 0; i < this.accounts.length; i++) {
            html += `<li data-value="${this.accounts[i].privacyName}">${this.accounts[i].privacyName}</li>`;
        }

        const ul = this.accountSelectListElement.querySelector('ul');
        if (ul) {
            ul.innerHTML = html;
        }

        this.accountSelectListElement.classList.remove('hidden');
        this.accountSelectListElement.classList.remove('disabled');
        this.submitButtonElement?.classList.remove('disabled');

        setTimeout(() => {
            this.accountSelectListElement.querySelectorAll('li').forEach((el: HTMLElement) => {
                el.addEventListener('click', () => {
                    this.preventPersistingAccount = false;
                    this.selectAccount(el);
                    this.onModelChange(true);
                    this.setPnlSelectedAccount();
                });
            });

            if (!this.accountRendered) {
                this.accountRendered = true;
                this.setAccountFromChartStyles();
            } else {
                const accountElement = this.accountSelectListElement.findFirst(`li[data-value="${this.model.account}"]`);
                if (!accountElement || (this.model.account == accountElement.dataset.value && this.model.accountLabel == accountElement.innerHTML)) {
                    return;
                }

                this.selectAccount(accountElement);
            }
        }, 200);
    }

    private setPnlSelectedAccount() {
        const pnl = this.base.getComponentsByType(PnlComponent)[0] as PnlComponent;
        if (pnl) {
            pnl.setSelectedAccountFromCurrentPositionActiveElement();
        }
    }

    private setPnlLastTradeAccount() {
        const pnl = this.base.getComponentsByType(PnlComponent)[0] as PnlComponent;
        if (pnl) {
            pnl.lastTradeAccount = this.model.account;
            pnl.setSelectPositionByAccount(this.model.account);
        }
    }

    private selectAccount(el: HTMLElement) {
        const parent = el.closest('.select-list');
        if (parent) {
            const label = parent.querySelector('p');
            if (label) {
                label.innerHTML = el.innerHTML;
            }
        }

        this.model.account = el.dataset.value;
        this.model.accountLabel = el.innerHTML;
    }

    private initAutoNumberic() {
        const quoteQuantityInputElement = this.element.querySelector<HTMLElement>('.form-group .amount');
        const pricesInputElement = this.element.querySelector<HTMLElement>('.form-group .prices');

        if (pricesInputElement) {
            this.pricesInput = new AutoNumeric(pricesInputElement, null, {
                wheelStep: 0.01,
                minimumValue: '0'
            });
        }

        if (quoteQuantityInputElement) {
            this.quoteQuantityInput = new AutoNumeric(quoteQuantityInputElement, this.model.quoteQuantity, {
                decimalPlaces: 0,
                wheelStep: 1
            });
        }
    }

    private setDefaults() {
        this.model.price = this.model.defaultPrice;

        if (this.pricesInput) {
            this.pricesInput.set(this.model.defaultPrice);
        }

        if (this.quoteQuantityInput) {
            this.quoteQuantityInput.set(this.model.quoteQuantity);
        }

        this.model.orderType = this.model.defaultOrderType;
        this.element.querySelectorAll<HTMLElement>('.trade-options .opt').forEach((el: HTMLElement) => {
            if (el.getAttribute('data-value') == this.model.defaultOrderType) {
                el.classList.add('active');
            } else {
                el.classList.remove('active');
            }
        });

        this.model.tif = this.model.defaultTIF;
        this.setSelectListValue('.select-list.tif li', this.model.defaultTIF);

        this.model.quoteType = this.model.defaultQuoteType;
        this.setSelectListValue('.select-list.quote-type li', this.model.defaultQuoteType);

        // this.handleSharesOrDollarsWording();
        this.updatePriceLabel();
        this.handlePriceInput();
        this.setSubmitButtonText();

        this.onModelChange(true);
    }

    private setSelectListValue(querySelector: string, defaultValue: string) {
        this.element.querySelectorAll<HTMLElement>(querySelector).forEach((el: HTMLElement) => {
            if (el.getAttribute('data-value') == defaultValue) {
                const parent = el.closest('.select-list')?.querySelector('p');
                if (parent) {
                    parent.innerHTML = el.innerHTML;
                }

                el.classList.add('active');
            } else {
                el.classList.remove('active');
            }
        });
    }

    private updatePriceLabel() {
        if (this.priceLabelElement) {
            this.priceLabelElement.innerHTML = FormatHelpers.numberToStringThousandSeparators(this.model.defaultPrice, true);
        }
    }

    private onPriceLabelClick() {
        this.priceLabelElement?.addEventListener('click', () => {
            if (this.model.orderType == TradeOrderType.Market) return;
            this.pricesInput?.set(this.model.defaultPrice);
            this.model.price = this.model.defaultPrice;
        });
    }

    private onPricesKeyup() {
        this.pricesInput?.node().addEventListener('autoNumeric:rawValueModified', () => {
            this.model.price = this.pricesInput.getNumber();
            this.setSubmitButtonText();
        });
        this.pricesInput?.node().addEventListener('keyup', () => {
            this.model.price = this.pricesInput.getNumber();
            this.setSubmitButtonText();
        });
        this.pricesInput?.node().addEventListener('change', () => {
            this.model.price = this.pricesInput.getNumber();
            this.setSubmitButtonText();
        });
    }

    private onSharesAmountKeyup() {
        this.quoteQuantityInput?.node().addEventListener('keyup', () => {
            this.preventPersistingShares = false;
            this.model.quoteQuantity = this.quoteQuantityInput.getNumber();
            this.onModelChange(true);
        });
    }

    private setSubmitButtonText() {
        const isBuy = this.element.classList.contains('buy');
        let text = 'Send Order';
        if (this.model.orderType == TradeOrderType.Market) {
            text = `${isBuy ? 'Buy' : 'Sell'} Now`;
        }

        const textElement = this.submitButtonElement?.querySelector('p');
        textElement && (textElement.innerHTML = text);
    }

    private setBuy() {
        this.model.isBuy = true;
        this.element.classList.remove('sell');
        this.element.classList.add('buy');
        this.setSubmitButtonText();
    }

    private setSell() {
        this.model.isBuy = false;
        this.element.classList.remove('buy');
        this.element.classList.add('sell');
        this.setSubmitButtonText();
    }

    private onBuySellClick() {
        const buyBtn = this.element.querySelector<HTMLElement>('.buy-sell .buy');
        const sellBtn = this.element.querySelector<HTMLElement>('.buy-sell .sell');

        buyBtn?.addEventListener('click', () => {
            if (this.moving || this.element.classList.contains('buy')) {
                return;
            }

            this.setBuy();
        });

        sellBtn?.addEventListener('click', () => {
            if (this.moving || this.element.classList.contains('sell')) {
                return;
            }

            this.setSell();
        });
    }

    private onTradeOptionsClick() {
        const options = this.element.querySelectorAll<HTMLElement>('.trade-options .opt');
        for (const op of Array.from(options)) {
            op.addEventListener('click', () => {
                this.selectTradeOption(op);
            });
        }
    }

    private selectTradeOption(op: HTMLElement) {
        if (!op || op.classList.contains('active')) return;

        const options = this.element.querySelectorAll<HTMLElement>('.trade-options .opt');
        if (this.moving || op.classList.contains('disabled')) return;
        options.forEach((o: HTMLElement) => {
            o.classList.remove('active');
        });

        op.classList.add('active');
        this.model.orderType = op.getAttribute('data-value') as TradeOrderType;
        this.handlePriceInput();
        this.setSubmitButtonText();
    }

    private handlePriceInput() {
        const label = this.pricesInput?.parent()?.querySelector('.label');
        if (!label) return;
        if (this.model.orderType == TradeOrderType.Market) {
            this.pricesInput.clear();
            this.pricesInput.node().classList.add('disabled');
            this.pricesInput.node().setAttribute('placeholder', 'Market');
            this.pricesInput.update({ readOnly: true });
            label.innerHTML = 'Price'
        } else {
            this.model.price = this.model.defaultPrice;
            this.pricesInput.set(this.model.defaultPrice);
            this.pricesInput.node().classList.remove('disabled');
            this.pricesInput.node().setAttribute('placeholder', '');
            this.pricesInput.update({ readOnly: false });
            label.innerHTML = 'Price';
        }
    }

    private onSelectListClick() {
        this.element.querySelectorAll<HTMLElement>('.select-list').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                if (this.moving || el.classList.contains('disabled')) return;
                const isActive = el.classList.contains('active');
                this.removeAllActiveSelectList();
                if (!isActive) {
                    el.classList.add('active');
                }
            });
        });
    }

    private onSelectListItemClick() {
        this.element.querySelectorAll<HTMLElement>('.select-list ul li').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                const parent = el.closest('.select-list');
                if (parent) {
                    const label = parent.querySelector('p');
                    if (label) {
                        label.innerHTML = el.innerHTML;
                    }

                    if (parent.classList.contains('tif')) {
                        this.model.tif = el.dataset.value;
                    } else if (parent.classList.contains('quote-type')) {
                        this.preventPersistingShares = false;
                        this.model.quoteType = el.dataset.value;
                        this.setSubmitButtonText();
                    }

                    this.onModelChange(true);
                }
            });
        });
    }

    private removeAllActiveSelectList() {
        this.element.querySelectorAll<HTMLElement>('.select-list').forEach((el: HTMLElement) => {
            el.classList.remove('active');
        });
    }

    private validate(): boolean {
        if (!this.getQuoteQuantity()) return false;
        return true;
    }

    private onSubmitButtonClick() {
        this.submitButtonElement?.addEventListener('click', () => {
            if (!this.validate()) {
                this.quoteQuantityInput.node().focus();
                return;
            }

            if (this.submitButtonElement.classList.contains('disabled')) return;

            this.submitting = true;
            const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
            let chartSettingsPopupModel = this.base.tiChartSeries?.chartStyleSettings?.chartSettingsPopupModel;
            if (!chartSettingsPopupModel) {
                chartSettingsPopupModel = new ChartSettingsPopupModel();
            }

            if (popup && chartSettingsPopupModel.displayOrderConfirmationPopup) {
                const message = TradeOrderMessageHelpers.getMessage(
                    this.model.isBuy,
                    this.model.quoteType,
                    this.model.orderType,
                    this.model.quoteQuantity,
                    this.model.tif,
                    this.base.tiChartSeries.symbol,
                    this.model.price,
                    this.model.account);
                popup.show(message.title, message.message, (dontShowMessage: boolean) => {
                    chartSettingsPopupModel.displayOrderConfirmationPopup = !dontShowMessage;
                    this.base.chartService.requestSaveChartSettingsPopupModel(chartSettingsPopupModel);

                    this.requestTrade();
                }, InformationPopupType.TradeConfirmation);
            } else {
                this.requestTrade();
            }
        });
    }

    private requestTrade() {
        this.setPnlLastTradeAccount();
        
        const model = ObjectHelpers.copy(this.model);
        model.isFromOEP = true;
        
        this.base.chartService.requestManualTrade(model)
        this.submitting = false;
    }

    private onDrag() {
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;

        this.element.addEventListener('mousedown', (e: MouseEvent) => {
            if (this.dragging) {
                return;
            }

            const targetElement = e.target as HTMLElement;
            if (targetElement && targetElement.classList.contains('no-d') || !!targetElement.closest('.no-d')) {
                return;
            }

            this.dragging = true;

            pos3 = e.clientX;
            pos4 = e.clientY;
        });

        document.body.addEventListener('mousemove', (e: MouseEvent) => {
            if (!this.dragging) {
                return;
            }

            pos1 = pos3 - e.clientX;
            pos2 = pos4 - e.clientY;
            pos3 = e.clientX;
            pos4 = e.clientY;

            const newLeft = this.element.offsetLeft - pos1;
            const newTop = this.element.offsetTop - pos2;

            // Check if the new position is within the container
            if (
                newLeft >= 0 &&
                newTop >= 0 &&
                newLeft + this.element.offsetWidth <= this.base.chartWrapper.offsetWidth &&
                newTop + this.element.offsetHeight <= this.base.chartWrapper.offsetHeight
            ) {
                this.element.style.left = newLeft + 'px';
                this.element.style.top = newTop + 'px';
                this.element.classList.add('dragging');
                this.moving = true;
            }
        });

        document.body.addEventListener('mouseleave', () => {
            if (!this.dragging) {
                return;
            }

            this.afterDragging();
        });

        document.body.addEventListener('mouseup', () => {
            if (!this.dragging) {
                return;
            }

            this.afterDragging();
        });
    }

    private afterDragging() {
        this.dragging = false;
        this.element.classList.remove('dragging');

        setTimeout(() => {
            this.saveUIState();
            this.moving = false;
            setTimeout(() => {
                this.checkIfElementOverflow();
            }, 300);
        }, 500);
    }

    private saveUIState() {
        if (!this.base.chartService || !this.chartStyle || !this.enabled) return;

        const isOpen = this.element.classList.contains('active');
        const top = this.element.style.top?.replace('px', '') ?? '';
        const left = this.element.style.left?.replace('px', '') ?? '';
        const account = this.accountRendered && !this.preventPersistingAccount ? this.model.account : this.chartStyle.newTradeUIAccount;
        this.base.chartService.requestSaveTradeUIState(
            isOpen,
            top ? parseFloat(top) : -9999.99,
            left ? parseFloat(left) : -9999.99,
            this.model.tif,
            account);
    }

    private onResize() {
        window.addEventListener('resize', () => {
            this.removeAllActiveSelectList();
        })
    }

    private onExtreamChange() {
        this.base.tiChartSeries.subscribeRedrawChange(this.id, () => {
            this.checkIfElementOverflow();
        });
    }

    private checkIfElementOverflow() {
        if (this.chartStyle.primitiveMode) {
            return;
        }

        const child = this.element;
        const parentRect = document.body.getBoundingClientRect();
        const childRect = child.getBoundingClientRect();

        if (
            !(childRect.top >= 0 &&
                childRect.bottom <= parentRect.height &&
                childRect.left >= parentRect.left &&
                childRect.right <= parentRect.right) || !(childRect.width && childRect.height)
        ) {

            this.element.style.left = this.base.marketingWebChartMode ? this.DEFAULT_BROWSER_LEFT : this.DEFAULT_LEFT;
            this.element.style.top = this.base.marketingWebChartMode ? this.DEFAULT_BROWSER_TOP : this.DEFAULT_TOP;
            this.saveUIState();
        }
    }

    private onBodyClick() {
        document.body.addEventListener('click', (e: any) => {
            if (!e || !e.target || typeof e.target.classList === 'undefined') {
                return;
            }

            if (!(e.target.classList.contains('select-list') || !!e.target.closest('.select-list'))) {
                this.removeAllActiveSelectList();
            }
        });
    }

    private renderHtml() {
        const arrow = `
            <span class="arrow">
                <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" x="0px" y="0px" viewBox="0 -4.5 20 20">
                    <g xmlns="http://www.w3.org/2000/svg" transform="translate(-180.000000, -6684.000000)" fill="rgb(163, 163, 163)">
                        <g id="icons" transform="translate(56.000000, 160.000000)">
                            <path d="M144,6525.39 L142.594,6524 L133.987,6532.261 L133.069,6531.38 L133.074,6531.385 L125.427,6524.045 L124,6525.414 C126.113,6527.443 132.014,6533.107 133.987,6535 C135.453,6533.594 134.024,6534.965 144,6525.39" id="arrow_down-[#339]"></path>
                        </g>
                    </g>
                </svg>
            </span>
        `;
        const html = `
            <div class="wrapper">
                <div class="buy-sell no-d">
                    <div class="sell no-d">
                        <p>Sell</p>
                    </div>
                    <span class="value no-d">4.05</span>
                    <div class="buy no-d">
                        <p>Buy</p>
                    </div>
                </div>
                <div class="trade-options no-d">
                    <div class="opt no-d" data-value="Stop">Stop</div>
                    <div class="opt no-d active" data-value="Market">Market</div>
                    <div class="opt no-d" data-value="Limit">Limit</div>
                </div>
                <div class="form-group">
                    <p class="label">Price</p>
                    <input type="text" title="" inputmode="decimal" pattern="[0-9]*" value="0" class="prices no-d" />
                </div>
                <div class="form-group">
                    <div class="select-list quote-type no-d left">
                        <p>Shares</p>
                        ${arrow}
                        <ul>
                            <li data-value="Shares">Shares</li>
                            <li data-value="Dollars">Dollars</li>
                        </ul>
                    </div>
                    <input type="text" title="" inputmode="decimal" pattern="[0-9]*" value="" class="amount no-d" />
                </div>
                <div class="form-group">
                    <p class="label">TIF</p>
                    <div class="select-list tif no-d">
                        <p>Day</p>
                        ${arrow}
                        <ul>
                            <li data-value="Day">Day</li>
                            <li data-value="Week">Week</li>
                        </ul>
                    </div>
                </div>
                <div class="select-list hidden disabled accounts no-d">
                    <p>&nbsp;</p>
                    ${arrow}
                    <ul></ul>
                </div>
                <div class="submit-button no-d disabled">
                    <p></p>
                </div>
            </div>
        `;

        const div = document.createElement("DIV");
        div.className = 'trade-component';

        div.style.left = this.base.marketingWebChartMode ? this.DEFAULT_BROWSER_LEFT : this.DEFAULT_LEFT;
        div.style.top = this.base.marketingWebChartMode ? this.DEFAULT_BROWSER_TOP : this.DEFAULT_TOP;
        div.innerHTML = html;

        this.element = div;

        this.base.chartWrapper?.insertAdjacentElement('beforeend', this.element);

        this.model.isBuy = true;
    }
}